'use client';

import { useCallback, useRef } from 'react';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash.debounce';

const DEFAULT_DEBOUNCE_DELAY = 500;

interface DebouncedCallbackWithFlush<T extends unknown[]> {
  debouncedCallback: (...args: T) => void;
  flush: () => void;
  cancel: () => void;
}

export const useLodashDebouncedCallback = <T extends unknown[]>(
  callback: (...args: T) => Promise<void> | void,
  wait: number = DEFAULT_DEBOUNCE_DELAY,
  leading = false,
): DebouncedCallbackWithFlush<T> => {
  const debouncedFn = useRef<DebouncedFunc<(...args: T) => void>>(
    debounce(callback, wait, { leading, trailing: true }),
  );

  const debouncedCallback = useCallback((...args: T) => {
    if (debouncedFn.current) {
      void debouncedFn.current(...args);
    }
  }, []);

  const flush = useCallback(() => {
    if (debouncedFn.current) {
      debouncedFn.current.flush();
    }
  }, []);

  const cancel = useCallback(() => {
    if (debouncedFn.current) {
      debouncedFn.current.cancel();
    }
  }, []);

  return { debouncedCallback, flush, cancel };
};
